// PostHog browser SDK wrapper.
// - Initialized only in production, only in the browser (SSR-safe).
// - Autocapture + Session Replay + automatic pageviews are enabled.
// - identify() / reset() / capture() are safe no-ops when disabled.
type PH = {
  identify: (id: string, props?: Record<string, unknown>) => void;
  reset: () => void;
  capture: (event: string, props?: Record<string, unknown>) => void;
};

let phInstance: PH | null = null;
let initPromise: Promise<PH | null> | null = null;

function shouldInit(): boolean {
  if (typeof window === "undefined") return false;
  const env = import.meta.env as Record<string, string | undefined>;
  const forced = env.VITE_POSTHOG_FORCE === "1" || env.VITE_POSTHOG_FORCE === "true";
  if (!import.meta.env.PROD && !forced) return false;
  return Boolean(env.VITE_POSTHOG_KEY);
}

export function initPostHog(): void {
  if (!shouldInit() || initPromise) return;
  const env = import.meta.env as Record<string, string | undefined>;
  initPromise = import("posthog-js").then((mod) => {
    const posthog = mod.default;
    // Route all PostHog traffic through the managed reverse proxy.
    // Improves delivery reliability and sidesteps most ad-blockers.
    // `ui_host` keeps toolbar/session-replay deep links pointing at the
    // real PostHog dashboard.
    posthog.init(env.VITE_POSTHOG_KEY!, {
      api_host: "https://t.lumvio.co",
      ui_host: "https://us.posthog.com",
      capture_pageview: "history_change",
      capture_pageleave: true,
      autocapture: true,
      session_recording: { maskAllInputs: true },
      disable_session_recording: false,
      persistence: "localStorage+cookie",
      person_profiles: "identified_only",
    });
    phInstance = posthog as unknown as PH;
    return phInstance;
  }).catch(() => null);
}

function withPH(fn: (ph: PH) => void): void {
  if (phInstance) { try { fn(phInstance); } catch { /* noop */ } return; }
  if (initPromise) {
    initPromise.then((ph) => { if (ph) { try { fn(ph); } catch { /* noop */ } } });
  }
}

export type PHUserProps = {
  email?: string | null;
  role?: string | null;
  subscription_plan?: string | null;
  [key: string]: unknown;
};

export function identifyPostHog(userId: string, props: PHUserProps = {}): void {
  withPH((ph) => ph.identify(userId, props));
}

export function resetPostHog(): void {
  withPH((ph) => ph.reset());
}

export function capturePostHog(
  event: string,
  props?: Record<string, unknown>,
): void {
  withPH((ph) => ph.capture(event, props));
}

// Well-known event names — use these to avoid typos at call sites.
export const PHEvents = {
  SignupCompleted: "signup_completed",
  Login: "login",
  Logout: "logout",
  KycStarted: "kyc_started",
  KycVerified: "kyc_verified",
  PortfolioCreated: "portfolio_created",
  ProjectCreated: "project_created",
  ProjectCompleted: "project_completed",
  PaymentStarted: "payment_started",
  PaymentCompleted: "payment_completed",
  EscrowReleased: "escrow_released",
} as const;
